<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import RosterDetailsModal from './RosterDetailsModal.vue';
import TournamentRosterCard from '@tournaments/TournamentRosterCard.vue';
import Avatar from '../shared/Avatar.vue';

interface Participant {
  id: number;
  user_id: number;
  name: string;
  avatar_url?: string;
  approved: boolean | null;
  created_at: string;
  roster?: {
    id: number;
    hero_name?: string;
    faction_name: string;
    structure: any;
    tournament_roster_url?: string;
  } | null;
  rosters?: {
    id: number;
    hero_name?: string;
    faction_name: string;
    structure: any;
    tournament_roster_url?: string;
  }[];
}

interface Props {
  eventId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'action-loading': [loading: boolean];
  'participants-updated': [];
}>();

const { t, locale } = useI18n();
const participants = ref<Participant[]>([]);
const isLoading = ref(true);
const error = ref<string | null>(null);
const actionLoadingStates = ref<Record<number, boolean>>({});

const isAnyActionLoading = computed(() => {
  return Object.values(actionLoadingStates.value).some(loading => loading);
});

const loadParticipants = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await axios.get(`/api/v1/events/${props.eventId}/participants`);
    participants.value = response.data;
  } catch (err) {
    console.error('Error loading participants:', err);
    error.value = t('participant_management.error_loading_participants');
  } finally {
    isLoading.value = false;
  }
};

const approveParticipant = async (userId: number, event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  // Find the participant to check their current status
  const participant = participants.value.find(p => p.user_id === userId);
  const wasRejected = participant?.approved === false;

  // Show confirmation for approving rejected participants
  if (wasRejected) {
    const confirmed = confirm(t('participant_management.confirm_approve_rejected'));
    if (!confirmed) {
      return;
    }
  }

    try {
    actionLoadingStates.value[userId] = true;

    await axios.post(`/api/v1/events/${props.eventId}/approve_participant`, { user_id: userId });

    // Update participant status locally instead of reloading
    if (participant) {
      participant.approved = true;
    }

    // Emit event to notify parent components
    emit('participants-updated');
  } catch (err: any) {
    console.error('Error approving participant:', err);

    // Handle different types of errors
    if (err.response?.status === 401 || err.response?.status === 302) {
      error.value = t('participant_management.error_authorization');
    } else if (err.response?.status === 403) {
      error.value = t('participant_management.error_permission');
    } else if (err.response?.data?.error) {
      error.value = t('participant_management.error_with_message', { message: err.response.data.error });
    } else {
      error.value = t('participant_management.error_approving');
    }
  } finally {
    actionLoadingStates.value[userId] = false;
  }
};

const rejectParticipant = async (userId: number, event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  // Find the participant to check their current status
  const participant = participants.value.find(p => p.user_id === userId);
  const isApproved = participant?.approved === true;

  // Show confirmation for rejecting approved participants
  if (isApproved) {
    const confirmed = confirm(t('participant_management.confirm_reject_approved'));
    if (!confirmed) {
      return;
    }
  }

  try {
    actionLoadingStates.value[userId] = true;
    await axios.post(`/api/v1/events/${props.eventId}/reject_participant`, { user_id: userId });

    // Update participant status locally instead of reloading
    if (participant) {
      participant.approved = false;
    }

    // Emit event to notify parent components
    emit('participants-updated');
    } catch (err: any) {
    // Handle different types of errors
    if (err.response?.status === 401 || err.response?.status === 302) {
      error.value = t('participant_management.error_authorization');
    } else if (err.response?.status === 403) {
      error.value = t('participant_management.error_permission');
    } else if (err.response?.data?.error) {
      error.value = t('participant_management.error_with_message', { message: err.response.data.error });
    } else {
      error.value = t('participant_management.error_rejecting');
    }
  } finally {
    actionLoadingStates.value[userId] = false;
  }
};

const removeParticipant = async (userId: number, event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  // Find the participant to check their current status
  const participant = participants.value.find(p => p.user_id === userId);
  const statusKey = participant?.approved === null ? 'pending' :
                 participant?.approved === true ? 'approved' : 'rejected';

  // Show confirmation for removing participant
  const confirmed = confirm(t('participant_management.confirm_remove', { status: t(`participant_management.status_${statusKey}`) }));
  if (!confirmed) {
    return;
  }

  try {
    actionLoadingStates.value[userId] = true;
    await axios.delete(`/api/v1/events/${props.eventId}/remove_participant`, { data: { user_id: userId } });
    // Remove participant from local list
    const index = participants.value.findIndex(p => p.user_id === userId);
    if (index !== -1) {
      participants.value.splice(index, 1);
    }

    // Emit event to notify parent components
    emit('participants-updated');
  } catch (err: any) {
    console.error('Error removing participant:', err);

    // Handle different types of errors
    if (err.response?.status === 401 || err.response?.status === 302) {
      error.value = t('participant_management.error_authorization');
    } else if (err.response?.status === 403) {
      error.value = t('participant_management.error_permission');
    } else if (err.response?.data?.error) {
      error.value = t('participant_management.error_with_message', { message: err.response.data.error });
    } else {
      error.value = t('participant_management.error_removing');
    }
  } finally {
    actionLoadingStates.value[userId] = false;
  }
};

const getStatusText = (approved: boolean | null) => {
  if (approved === null) return t('participant_management.status_pending');
  if (approved) return t('participant_management.status_approved');
  return t('participant_management.status_rejected');
};

const getStatusClass = (approved: boolean | null) => {
  if (approved === null) return 'status-pending';
  if (approved) return 'status-approved';
  return 'status-rejected';
};

const getStatusSummary = computed(() => {
  const pending = participants.value.filter(p => p.approved === null).length;
  const approved = participants.value.filter(p => p.approved === true).length;
  const rejected = participants.value.filter(p => p.approved === false).length;

  return { pending, approved, rejected, total: participants.value.length };
});

const showRosterModal = ref(false);
const selectedRoster = ref<any>(null);

const showRosterDetails = (roster: any) => {
  selectedRoster.value = roster;
  showRosterModal.value = true;
};

const closeRosterModal = () => {
  showRosterModal.value = false;
  selectedRoster.value = null;
};

const openRosterLink = (roster: any) => {
  if (roster.tournament_roster_url) {
    window.open(`/tournament-roster/${roster.tournament_roster_url}`, '_blank');
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  // Check if date is valid (not NaN or Invalid Date)
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString(locale.value === 'en' ? 'en-US' : 'ru-RU');
};

// Watch for changes in loading state and emit to parent
watch(isAnyActionLoading, (loading) => {
  emit('action-loading', loading);
});

onMounted(() => {
  loadParticipants();
});
</script>

<template>
  <div class="participant-management">
    <h3>{{ t('participant_management.title') }}</h3>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <div v-if="!isLoading && participants.length > 0" class="status-summary">
      <div class="summary-item">
        <span class="summary-label">{{ t('participant_management.summary_total') }}:</span>
        <span class="summary-value">{{ getStatusSummary.total }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">{{ t('participant_management.summary_pending') }}:</span>
        <span class="summary-value pending">{{ getStatusSummary.pending }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">{{ t('participant_management.summary_approved') }}:</span>
        <span class="summary-value approved">{{ getStatusSummary.approved }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">{{ t('participant_management.summary_rejected') }}:</span>
        <span class="summary-value rejected">{{ getStatusSummary.rejected }}</span>
      </div>
    </div>

    <div v-if="isLoading" class="loading">
      {{ t('participant_management.loading_participants') }}
    </div>

    <div v-else-if="participants.length === 0" class="no-participants">
      {{ t('participant_management.no_participants') }}
    </div>

    <div v-else class="participants-list">
      <div
        v-for="participant in participants"
        :key="participant.id"
        class="participant-item"
      >
        <div class="participant-info">
          <div class="participant-header">
            <Avatar
              :src="participant.avatar_url"
              :alt="participant.name"
              size="small"
              :show-frame="true"
              shape="hexagon"
              fallback-icon="bi-person"
            />
            <div class="participant-details">
              <div class="participant-name">{{ participant.name }}</div>
              <div class="participant-date">
                {{ t('participant_management.application') }}: {{ formatDate(participant.created_at) }}
              </div>
            </div>
          </div>
          <div v-if="participant.rosters && participant.rosters.length > 0" class="participant-rosters">
            <div class="rosters-header">
              <span class="rosters-count">{{ participant.rosters.length }} {{ participant.rosters.length === 1 ? t('participant_management.roster_singular') : t('participant_management.rosters_plural') }}</span>
            </div>
            <div class="rosters-grid">
              <TournamentRosterCard
                v-for="(roster, index) in participant.rosters"
                :key="roster.id"
                :roster="roster"
                :roster-index="index"
                :is-active="false"
                :is-clickable="false"
                :show-index="true"
                @roster-view="showRosterDetails"
                @roster-link="openRosterLink"
              />
            </div>
          </div>
          <div v-else-if="participant.roster" class="participant-roster">
            <div class="roster-hero">{{ participant.roster.hero_name || t('participant_management.hero_not_specified') }}</div>
            <div class="roster-faction">{{ participant.roster.faction_name }}</div>
            <div class="roster-actions">
              <button
                class="btn btn-small btn-info"
                @click="showRosterDetails(participant.roster)"
              >
                {{ t('participant_management.show_roster') }}
              </button>
              <a
                v-if="participant.roster.tournament_roster_url"
                :href="`/tournament-roster/${participant.roster.tournament_roster_url}`"
                target="_blank"
                class="btn btn-small btn-secondary"
                :title="t('participant_management.open_roster_new_tab')"
              >
                <i class="bi bi-box-arrow-up-right"></i>
                {{ t('participant_management.open') }}
              </a>
            </div>
          </div>
        </div>

        <div class="participant-status">
          <span :class="['status-badge', getStatusClass(participant.approved)]">
            {{ getStatusText(participant.approved) }}
          </span>
        </div>

        <div class="participant-actions" @click.stop>
          <button
            v-if="participant.approved === null"
            type="button"
            @click.stop="approveParticipant(participant.user_id, $event)"
            class="btn btn-approve"
            :disabled="actionLoadingStates[participant.user_id]"
          >
            <span v-if="actionLoadingStates[participant.user_id]" class="loading-spinner">⏳</span>
            {{ actionLoadingStates[participant.user_id] ? t('participant_management.approving') : t('participant_management.approve') }}
          </button>

          <button
            v-if="participant.approved === null"
            type="button"
            @click.stop="rejectParticipant(participant.user_id, $event)"
            class="btn btn-reject"
            :disabled="actionLoadingStates[participant.user_id]"
          >
            <span v-if="actionLoadingStates[participant.user_id]" class="loading-spinner">⏳</span>
            {{ actionLoadingStates[participant.user_id] ? t('participant_management.rejecting') : t('participant_management.reject') }}
          </button>

          <button
            v-if="participant.approved === true"
            type="button"
            @click.stop="rejectParticipant(participant.user_id, $event)"
            class="btn btn-reject-approved"
            :disabled="actionLoadingStates[participant.user_id]"
            :title="t('participant_management.reject_approved_title')"
          >
            <span v-if="actionLoadingStates[participant.user_id]" class="loading-spinner">⏳</span>
            {{ actionLoadingStates[participant.user_id] ? t('participant_management.rejecting') : t('participant_management.cancel_participation') }}
          </button>

          <button
            v-if="participant.approved === false"
            type="button"
            @click.stop="approveParticipant(participant.user_id, $event)"
            class="btn btn-approve-rejected"
            :disabled="actionLoadingStates[participant.user_id]"
            :title="t('participant_management.approve_rejected_title')"
          >
            <span v-if="actionLoadingStates[participant.user_id]" class="loading-spinner">⏳</span>
            {{ actionLoadingStates[participant.user_id] ? t('participant_management.approving') : t('participant_management.approve') }}
          </button>

          <button
            v-if="participant.approved !== null"
            type="button"
            @click.stop="removeParticipant(participant.user_id, $event)"
            class="btn btn-remove"
            :disabled="actionLoadingStates[participant.user_id]"
            :title="t('participant_management.remove_title')"
          >
            <span v-if="actionLoadingStates[participant.user_id]" class="loading-spinner">⏳</span>
            {{ actionLoadingStates[participant.user_id] ? t('participant_management.removing') : t('participant_management.remove') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Roster Details Modal -->
  <RosterDetailsModal
    :roster="selectedRoster"
    :is-visible="showRosterModal"
    @close="closeRosterModal"
  />
</template>

<style lang="scss" scoped>
.participant-management {
  padding: 20px;
  background-color: #efd8b0;
  border-radius: 8px;
  border: 1px solid #bd9c65;
}

h3 {
  color: #60451f;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: bold;
}

.status-summary {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.summary-label {
  font-size: 12px;
  color: #666;
  font-weight: bold;
}

.summary-value {
  font-size: 18px;
  font-weight: bold;
  color: #60451f;

  &.pending {
    color: #f57c00;
  }

  &.approved {
    color: #2e7d32;
  }

  &.rejected {
    color: #c62828;
  }
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #ffcdd2;
}

.loading, .no-participants {
  text-align: center;
  color: #bd9c65;
  padding: 20px;
}

.participants-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.participant-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #bd9c65;
  gap: 15px;
}

.participant-info {
  flex: 1;
  min-width: 0;
}

.participant-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}



.participant-details {
  display: flex;
  flex-direction: column;
}

.participant-name {
  font-weight: bold;
  color: #60451f;
  margin-bottom: 4px;
}

.participant-date {
  color: #bd9c65;
  font-size: 12px;
}

.participant-roster {
  margin-top: 8px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #ddd;

  .roster-hero {
    font-weight: bold;
    color: #60451f;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .roster-faction {
    color: #bd9c65;
    font-size: 12px;
    margin-bottom: 6px;
  }

  .roster-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }
}

.participant-rosters {
  margin-top: 8px;

  .rosters-header {
    margin-bottom: 8px;

    .rosters-count {
      font-weight: bold;
      color: #60451f;
      font-size: 14px;
      background-color: #e8f5e8;
      padding: 4px 8px;
      border-radius: 4px;
      border: 1px solid #4caf50;
    }
  }

  .rosters-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
}

// Responsive design
@media (max-width: 768px) {
  .participant-management {
    padding: 15px;
  }

  h3 {
    font-size: 16px;
    margin-bottom: 15px;
  }

  .status-summary {
    flex-direction: column;
    gap: 10px;
    padding: 12px;
  }

  .summary-item {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
  }

  .summary-item:last-child {
    border-bottom: none;
  }

  .summary-label {
    font-size: 14px;
  }

  .summary-value {
    font-size: 16px;
  }

  .participants-list {
    gap: 12px;
  }

  .participant-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px;
  }

  .participant-info {
    width: 100%;
  }

  .participant-header {
    margin-bottom: 10px;
  }

  .participant-name {
    font-size: 14px;
  }

  .participant-date {
    font-size: 11px;
  }

  .participant-rosters {
    margin-top: 10px;

    .rosters-header {
      margin-bottom: 6px;
    }

    .rosters-count {
      font-size: 12px;
      padding: 3px 6px;
    }

    .rosters-grid {
      grid-template-columns: 1fr;
      gap: 6px;
    }
  }

  .participant-status {
    align-self: flex-start;
  }

  .status-badge {
    font-size: 11px;
    padding: 3px 6px;
  }

  .participant-actions {
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }

  .btn {
    width: 100%;
    padding: 10px 12px;
    font-size: 14px;
    justify-content: center;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .loading-spinner {
    margin-right: 6px;
  }
}

@media (max-width: 480px) {
  .participant-management {
    padding: 10px;
  }

  h3 {
    font-size: 15px;
    margin-bottom: 12px;
  }

  .status-summary {
    padding: 10px;
    gap: 8px;
  }

  .summary-item {
    padding: 6px 0;
  }

  .summary-label {
    font-size: 13px;
  }

  .summary-value {
    font-size: 15px;
  }

  .participants-list {
    gap: 10px;
  }

  .participant-item {
    padding: 10px;
    gap: 10px;
  }

  .participant-name {
    font-size: 13px;
  }

  .participant-date {
    font-size: 10px;
  }

  .rosters-count {
    font-size: 11px;
    padding: 2px 5px;
  }

  .btn {
    padding: 8px 10px;
    font-size: 13px;
  }
}

.participant-status {
  flex-shrink: 0;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;

  &.status-pending {
    background-color: #fff3e0;
    color: #f57c00;
  }

  &.status-approved {
    background-color: #e8f5e8;
    color: #2e7d32;
  }

  &.status-rejected {
    background-color: #ffebee;
    color: #c62828;
  }
}

.participant-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-approve {
    background-color: #4caf50;
    color: white;

    &:hover:not(:disabled) {
      background-color: #45a049;
    }
  }

  &.btn-approve-rejected {
    background-color: #8bc34a;
    color: white;
    border: 2px solid #689f38;

    &:hover:not(:disabled) {
      background-color: #689f38;
    }
  }

  &.btn-reject {
    background-color: #f44336;
    color: white;

    &:hover:not(:disabled) {
      background-color: #da190b;
    }
  }

  &.btn-reject-approved {
    background-color: #ff9800;
    color: white;
    border: 2px solid #f57c00;

    &:hover:not(:disabled) {
      background-color: #f57c00;
    }
  }

  &.btn-remove {
    background-color: #d32f2f;
    color: white;
    border: 2px solid #b71c1c;

    &:hover:not(:disabled) {
      background-color: #b71c1c;
    }
  }

  &.btn-small {
    padding: 4px 8px;
    font-size: 11px;
  }

  &.btn-info {
    background-color: #2196f3;
    color: white;

    &:hover:not(:disabled) {
      background-color: #1976d2;
    }
  }
}

.loading-spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
  margin-right: 4px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
